/**
 * Every rule for six-digit codes, in one place: the sign-up code, the
 * Facebook sign-up popup's email code, password reset, and the 2FA pause.
 * Everything reads these numbers (the server checks, the messages under the
 * boxes, the screen text, the code emails, the resend countdown, the sign-up
 * cookie), so changing one here is the whole change. code-settings.test.ts
 * refuses values that don't fit together.
 *
 * Internal on purpose (not environment variables): nothing to configure per
 * server, and every environment behaves the same.
 */
export const CODE_SETTINGS = Object.freeze({
	/** How long a code works, in seconds. */
	ttlSeconds: 10 * 60,
	/** Wrong codes before a code locks (a new code gives fresh tries). */
	maxTries: 3,
	/** Wait before "Resend code" may send another code. */
	resendSeconds: 30,
	/**
	 * Codes one email (or phone) may get per window. A resend never resets
	 * the count, so this is what bounds guessing: maxTries x this per window.
	 */
	maxSendsPerWindow: 5,
	sendWindowMinutes: 60,
	/**
	 * How long a half-finished sign-up (or reset, or 2FA login) waits in the
	 * verify cookie. Never shorter than a code's life.
	 */
	verifySessionSeconds: 10 * 60,
})

/**
 * The 2FA login code (authenticator app). The secret is permanent, so it
 * pauses instead of locking: after `maxTries` wrong codes within
 * `windowMinutes` it waits until the window ends.
 */
export const TWO_FACTOR_SETTINGS = Object.freeze({
	maxTries: 5,
	windowMinutes: 15,
})

/** "1 minute" / "10 minutes", for text that states a duration. */
export function minutesText(minutes: number): string {
	return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`
}

/** A code's life as text: "10 minutes". */
export const CODE_LIFE_TEXT = minutesText(CODE_SETTINGS.ttlSeconds / 60)
